import React, { createContext, FC, useEffect, useMemo, useRef, useState } from "react";
import { groupBy } from "lodash";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { NestingParentDropdownItem, DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { formatEnumValue } from "@app/shared/utility/formatEnumValue";
import BenchmarkingMedicaidService from "@app/overviews/network-benchmarking-dashboard/benchmarkingMedicaidService";
import BenchmarkingQHPService from "@app/overviews/network-benchmarking-dashboard/benchmarkingQHPService";
import { QHPNetwork } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPNetwork";
import { MedicaidNetwork } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidNetwork";
import { BenchmarkingCounty } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingCounty";
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingLineOfBusinessEnum";
import { BenchmarkingState } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingState";
import { buildStateCountyData } from "@app/overviews/network-benchmarking-dashboard/filters/shared/buildStateCountyData";
import { CompetitiveInsightsTabTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/CompetitiveInsightsTabTypeEnum";
import { useBenchmarkingLinesOfBusiness } from "@app/overviews/network-benchmarking-dashboard/filters/hooks/useBenchmarkingLinesOfBusiness";
import { ContractDisplayInformation } from "@app/overviews/network-benchmarking-dashboard/filters/shared/ContractDisplayInformation";
import BenchmarkingService from "@app/overviews/network-benchmarking-dashboard/benchmarkingService";
import { BenchmarkingServiceType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/utility";
import { BenchmarkingSpecialtyGroup } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared/BenchmarkingSpecialtyGroup";
import { BenchmarkingNetworkType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingNetworkType";
import { BenchmarkingPrimaryFiltersPayloadType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingPrimaryFiltersPayloadType";
import { CompetitiveInsightsCountyFilterTypeEnum } from "./shared/CompetitiveInsightsCountyFilterType";

export interface BenchmarkingFiltersContextValues {
  affiliationAuthorizedStates: string[];
  counties: NestingParentDropdownItem[];
  countyIdFilter: number[] | null;
  displayedNetworkFilters: BenchmarkingNetworkType[];
  displayedPrimaryFilters: BenchmarkingPrimaryFiltersPayloadType;
  getContractDisplayInformation: (contractId: string) => ContractDisplayInformation;
  getService: () => BenchmarkingServiceType;
  isAffiliationState: () => boolean;
  linesOfBusiness: BenchmarkingLineOfBusinessEnum[];
  linesOfBusinessLoading: boolean;
  marketProjectId: number | null;
  mousedOverCountyIds: string[];
  mousedOverNetwork: BenchmarkingNetworkType;
  previousSelectedNetworks: BenchmarkingNetworkType[];
  processSpecialties: (specialtyGroups: BenchmarkingSpecialtyGroup[]) => void;
  programKey: string | null;
  rawCounties: BenchmarkingCounty[];
  rawSpecialties: BenchmarkingSpecialtyGroup[];
  selectedCounties: string[];
  selectedCountyFilterType: CompetitiveInsightsCountyFilterTypeEnum[];
  selectedLineOfBusiness: number;
  selectedNetworks: BenchmarkingNetworkType[];
  selectedState: BenchmarkingState;
  selectedTab: CompetitiveInsightsTabTypeEnum;
  setAffiliationAuthorizedStates: (states: string[]) => void;
  setCounties: (counties: NestingParentDropdownItem[]) => void;
  setCountyIdFilter: (countyIds: number[] | null) => void;
  setDisplayedNetworkFilters: (networkFilters: BenchmarkingNetworkType[]) => void;
  setDisplayedPrimaryFilters: (primaryFilters: BenchmarkingPrimaryFiltersPayloadType) => void;
  setMarketProjectId: (marketProjectId: number | null) => void;
  setMousedOverCountyIds: (countyIds: string[]) => void;
  setMousedOverNetwork: (network: BenchmarkingNetworkType) => void;
  setPreviousSelectedNetworks: (selectedHContracts: BenchmarkingNetworkType[]) => void;
  setProgramKey: (programKey: string | null) => void;
  setRawCounties: (rawCounties: BenchmarkingCounty[]) => void;
  setSelectedCounties: (selectedCounties: string[]) => void;
  setSelectedCountyFilterType: (selectedCountyFilterType: CompetitiveInsightsCountyFilterTypeEnum[]) => void;
  setSelectedLineOfBusiness: (selectedLineOfBusiness: number) => void;
  setSelectedNetworks: (selectedHContracts: BenchmarkingNetworkType[]) => void;
  setSelectedState: (state: BenchmarkingState) => void;
  setSelectedStateAndCounties: (state: BenchmarkingState, selectedCounties: string[]) => void;
  setSelectedTab: (selectedTab: CompetitiveInsightsTabTypeEnum) => void;
  setStates: (states: BenchmarkingState[]) => void;
  specialties: NestingParentDropdownItem[];
  states: BenchmarkingState[];
  toggleSelectedCountyFilterType: (filterType: CompetitiveInsightsCountyFilterTypeEnum) => void;
}

const initialValues: BenchmarkingFiltersContextValues = {
  affiliationAuthorizedStates: [],
  counties: [],
  countyIdFilter: null,
  displayedNetworkFilters: [],
  displayedPrimaryFilters: null,
  getContractDisplayInformation: () => null,
  getService: () => null,
  isAffiliationState: () => false,
  linesOfBusiness: [],
  linesOfBusinessLoading: false,
  marketProjectId: null,
  mousedOverCountyIds: [],
  mousedOverNetwork: null,
  previousSelectedNetworks: [],
  processSpecialties: () => null,
  programKey: null,
  rawCounties: [],
  rawSpecialties: [],
  selectedCounties: [],
  selectedCountyFilterType: [],
  selectedLineOfBusiness: null,
  selectedNetworks: [],
  selectedState: null,
  selectedTab: null,
  setAffiliationAuthorizedStates: () => null,
  setCounties: () => null,
  setCountyIdFilter: () => null,
  setDisplayedNetworkFilters: () => null,
  setDisplayedPrimaryFilters: () => null,
  setMarketProjectId: () => null,
  setMousedOverCountyIds: () => null,
  setMousedOverNetwork: () => null,
  setPreviousSelectedNetworks: () => null,
  setProgramKey: () => null,
  setRawCounties: () => null,
  setSelectedCounties: () => null,
  setSelectedCountyFilterType: () => null,
  setSelectedLineOfBusiness: () => null,
  setSelectedNetworks: () => null,
  setSelectedState: () => null,
  setSelectedStateAndCounties: () => null,
  setSelectedTab: () => null,
  setStates: () => null,
  specialties: [],
  states: [],
  toggleSelectedCountyFilterType: () => null,
}

export const BenchmarkingFiltersContext = createContext(initialValues);

export const BenchmarkingFiltersContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const [affiliationAuthorizedStates, setAffiliationAuthorizedStates] = useState<string[]>(initialValues.affiliationAuthorizedStates);
  const [counties, setCounties] = useState<NestingParentDropdownItem[]>(initialValues.counties);
  const [countyIdFilter, setCountyIdFilter] = useState<number[] | null>(initialValues.countyIdFilter);
  const [displayedNetworkFilters, setDisplayedNetworkFilters] = useState<BenchmarkingNetworkType[]>(initialValues.displayedNetworkFilters);
  const [displayedPrimaryFilters, setDisplayedPrimaryFilters] = useState<BenchmarkingPrimaryFiltersPayloadType>(initialValues.displayedPrimaryFilters);
  const [marketProjectId, setMarketProjectId] = useState<number | null>(initialValues.marketProjectId);
  const [mousedOverCountyIds, setMousedOverCountyIds] = useState<string[]>(initialValues.mousedOverCountyIds);
  const [mousedOverNetwork, setMousedOverNetwork] = useState<BenchmarkingNetworkType>(initialValues.mousedOverNetwork);
  const [previousSelectedNetworks, setPreviousSelectedNetworks] = useState<BenchmarkingNetworkType[]>(initialValues.previousSelectedNetworks);
  const [programKey, setProgramKey] = useState<string | null>(initialValues.programKey);
  const [rawCounties, setRawCounties] = useState<BenchmarkingCounty[]>(initialValues.rawCounties);
  const [rawSpecialties, setRawSpecialties] = useState<BenchmarkingSpecialtyGroup[]>(initialValues.rawSpecialties);
  const [selectedCounties, setSelectedCounties] = useState<string[]>(initialValues.selectedCounties);
  const [selectedCountyFilterType, setSelectedCountyFilterType] = useState<CompetitiveInsightsCountyFilterTypeEnum[]>([
    CompetitiveInsightsCountyFilterTypeEnum.Current,
  ]);
  const [selectedNetworks, setSelectedNetworks] = useState<BenchmarkingNetworkType[]>(initialValues.selectedNetworks);
  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState<number>(initialValues.selectedLineOfBusiness);
  const [selectedState, setSelectedState] = useState<BenchmarkingState>(initialValues.selectedState);
  const [selectedTab, setSelectedTab] = useState<CompetitiveInsightsTabTypeEnum>(CompetitiveInsightsTabTypeEnum.NetworkSelection);
  const [specialties, setSpecialties] = useState<NestingParentDropdownItem[]>(initialValues.specialties);
  const [states, setStates] = useState<BenchmarkingState[]>(initialValues.states);

  const benchmarkingQHPService = useMemo(() => new BenchmarkingQHPService(), []);
  const benchmarkingService = useMemo(() => new BenchmarkingService(), []);
  const benchmarkingMedicaidService = useMemo(() => new BenchmarkingMedicaidService(), []);

  // when a state change and an explicit county selection need to land atomically (e.g. applying advanced
  // filters staged in a flyout), this holds the explicit selection so the derive-counties effect below
  // uses it instead of resetting to "all counties in the new state"
  const explicitSelectedCountiesRef = useRef<string[] | null>(null);

  const { isLoading: linesOfBusinessLoading, linesOfBusiness } = useBenchmarkingLinesOfBusiness();

  useEffect(() => {
    const medicare: BenchmarkingLineOfBusinessEnum = linesOfBusiness.find(x => x === BenchmarkingLineOfBusinessEnum.Medicare);

    if (!!medicare) {
      setSelectedLineOfBusiness(medicare);
    }
  }, [linesOfBusiness]);

  useEffect(() => {
    if (previousSelectedNetworks.length) {
      setPreviousSelectedNetworks([]);
    }
  }, [selectedLineOfBusiness, selectedState]);

  useEffect(() => {
    if (!!selectedState) {
      const countyData = buildStateCountyData(selectedState, countyIdFilter);

      setRawCounties(countyData.rawCounties);
      setCounties(countyData.counties);

      if (explicitSelectedCountiesRef.current !== null) {
        setSelectedCounties(explicitSelectedCountiesRef.current);
        explicitSelectedCountiesRef.current = null;
      } else {
        setSelectedCounties(countyData.selectedCounties);
      }
    }
  }, [selectedState, countyIdFilter]);

  const getContractDisplayInformation = (contractId: string): ContractDisplayInformation => {
    let parentOrganizationName;
    let networkName;

    if (contractId?.includes("Market")) {
      return { networkName: contractId, parentOrganizationName: "QES" };
    }

    if (!!selectedNetworks?.length) {
      const network = selectedNetworks.find(x => x.contractId === contractId);

      if (!!network) {
        parentOrganizationName = network.parentOrganization;

        switch (selectedLineOfBusiness) {
          case BenchmarkingLineOfBusinessEnum.Marketplace:
            networkName = (network as QHPNetwork).networkName;
            break;
          case BenchmarkingLineOfBusinessEnum.Medicaid:
            networkName = (network as MedicaidNetwork).program;
            break;
          default:
            networkName = contractId;
            break;
        }
      }
    }

    return { networkName, parentOrganizationName };
  }

  const processSpecialties = (specialtyGroups: BenchmarkingSpecialtyGroup[]): void => {
    const nestedSpecialties: NestingParentDropdownItem[] = [];
    const groupedByCategory = groupBy(specialtyGroups, "category");
    const emptyValues: string[] = ["null", "undefined", ""];

    Object.entries(groupedByCategory).forEach((datum) => {
      const mappedSpecialties: DropdownItem<string>[] = datum[1].map(x => {
        return { key: x.specialtyGroupId.toString(), name: `${x.specialtyGroupCode} - ${x.specialtyGroupName}` }
      });

      const nestedSpecialty: NestingParentDropdownItem = {
        checkboxStatus: CheckboxStateEnum.Unchecked,
        children: mappedSpecialties,
        hideParent: emptyValues.includes(datum[0]) || Object.entries(groupedByCategory).length === 1,
        key: datum[0],
        name: formatEnumValue(datum[0]),
      }

      nestedSpecialties.push(nestedSpecialty);
    });

    // sort specialty categories alphabetically
    const sortedNestedSpecialties = nestedSpecialties.sort((a, b) => a.key.localeCompare(b.key));
    const facilityString = "facility";

    // if there is a facility category, then move it to the bottom of the grouped specialties list
    if (nestedSpecialties.map(x => x.key).some(x => x.toLowerCase().includes(facilityString))) {
      const facilityIndex: number = sortedNestedSpecialties.map(x => x.key).findIndex(x => x.toLowerCase().includes(facilityString));
      sortedNestedSpecialties.push(sortedNestedSpecialties.splice(facilityIndex, 1)[0]);
    }

    // if there are any values without a parent, display them alllll the way at the bottom
    const hiddenParent: NestingParentDropdownItem = nestedSpecialties.find(x => x.hideParent);

    if (!!hiddenParent) {
      const hiddenIndex: number = sortedNestedSpecialties.map(x => x.key).findIndex(x => emptyValues.includes(x));
      sortedNestedSpecialties.push(sortedNestedSpecialties.splice(hiddenIndex, 1)[0]);
    }

    setRawSpecialties(specialtyGroups);
    setSpecialties(sortedNestedSpecialties);
  }

  const getService = (): BenchmarkingServiceType => {
    switch (selectedLineOfBusiness) {
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        return benchmarkingQHPService;
      case BenchmarkingLineOfBusinessEnum.Medicare:
        return benchmarkingService;
      case BenchmarkingLineOfBusinessEnum.Medicaid:
        return benchmarkingMedicaidService;
      default:
        return;
    }
  }

  const isAffiliationState = (): boolean => {
    return affiliationAuthorizedStates?.includes(selectedState?.stateAbbreviation);
  }

  const setSelectedStateAndCounties = (state: BenchmarkingState, selectedCountiesToApply: string[]): void => {
    explicitSelectedCountiesRef.current = selectedCountiesToApply;
    setSelectedState(state);
  }

  const toggleSelectedCountyFilterType = (filterType: CompetitiveInsightsCountyFilterTypeEnum): void => {
    const isSelected = selectedCountyFilterType.includes(filterType);

    // at least one filter type must always remain selected
    if (isSelected && selectedCountyFilterType.length === 1) return;

    setSelectedCountyFilterType(
      isSelected
        ? selectedCountyFilterType.filter(x => x !== filterType)
        : [...selectedCountyFilterType, filterType]
    );
  }

  return (
    <BenchmarkingFiltersContext.Provider
      value={{
        affiliationAuthorizedStates,
        counties,
        countyIdFilter,
        displayedNetworkFilters,
        displayedPrimaryFilters,
        getContractDisplayInformation,
        getService,
        isAffiliationState,
        linesOfBusiness,
        linesOfBusinessLoading,
        marketProjectId,
        mousedOverCountyIds,
        mousedOverNetwork,
        previousSelectedNetworks,
        processSpecialties,
        programKey,
        rawCounties,
        rawSpecialties,
        selectedCounties,
        selectedCountyFilterType,
        selectedLineOfBusiness,
        selectedNetworks,
        selectedState,
        selectedTab,
        setAffiliationAuthorizedStates,
        setCounties,
        setCountyIdFilter,
        setDisplayedNetworkFilters,
        setDisplayedPrimaryFilters,
        setMarketProjectId,
        setMousedOverCountyIds,
        setMousedOverNetwork,
        setPreviousSelectedNetworks,
        setProgramKey,
        setRawCounties,
        setSelectedCounties,
        setSelectedCountyFilterType,
        setSelectedLineOfBusiness,
        setSelectedNetworks,
        setSelectedState,
        setSelectedStateAndCounties,
        setSelectedTab,
        setStates,
        specialties,
        states,
        toggleSelectedCountyFilterType,
      }}>
      {props.children}
    </BenchmarkingFiltersContext.Provider>
  )
}
